import HiddenApps from "@/widgets/hiddenApps/ui/HiddenApps/HiddenApps";
import styles from "./HiddenAppsView.module.scss";

import {
  useDeleteHiddenAppsMutation,
  useFetchHiddenAppsMutation,
} from "@/features/auth/api/authAPI";
import { AppCollectionAppsResponse as HiddenAppsResponse } from "@/features/auth/types/auth";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Loader from "@/shared/ui/Loader/Loader";

const PAGE_SIZE = 10;

const HiddenAppsView = () => {
  const [fetchHiddenApps, { isLoading: isLoadingApps }] =
    useFetchHiddenAppsMutation();
  const [deleteHiddenApp] = useDeleteHiddenAppsMutation();
  const [page, setPage] = useState(1);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const [hiddenApps, setHiddenApps] = useState<HiddenAppsResponse | null>(null);

  useEffect(() => {
    setHiddenApps(null);
    setPage(1);

    fetchHiddenApps({ page: 1, pageSize: PAGE_SIZE }).then((res) => {
      if (res.data) {
        setHiddenApps(res.data);
      }
    });
  }, [fetchHiddenApps]);

  const loadMoreApps = useCallback(async () => {
    if (isFetchingMore || !hiddenApps?.next) {
      return Promise.resolve();
    }

    setIsFetchingMore(true);
    const nextPage = page + 1;

    try {
      const response = await fetchHiddenApps({
        page: nextPage,
        pageSize: PAGE_SIZE,
      });

      if (response.data) {
        setHiddenApps((prevData) => {
          if (!prevData) return response.data;

          return {
            ...response.data,
            results: [...prevData.results, ...response.data.results],
          };
        });
        setPage(nextPage);
      }
    } catch (error) {
      console.error("Error loading more apps:", error);
    } finally {
      setIsFetchingMore(false);
    }

    return Promise.resolve();
  }, [page, PAGE_SIZE, isFetchingMore, hiddenApps?.next, fetchHiddenApps]);

  const handleRemoveHiddenApp = useCallback(
    async (appId: number) => {
      try {
        await deleteHiddenApp(appId).unwrap();

        // Remove the app from the local state
        setHiddenApps((prevHiddenApps) => {
          if (!prevHiddenApps) return prevHiddenApps;

          return {
            ...prevHiddenApps,
            results: prevHiddenApps.results.filter((app) => app.id !== appId),
          };
        });

        toast.success("App removed from hidden successfully!");
      } catch (error) {
        console.error("Error removing app from hidden:", error);
        toast.error("Failed to remove app from hidden");
      }
    },
    [deleteHiddenApp, setHiddenApps],
  );

  const showInitialLoader = isLoadingApps && !hiddenApps;

  console.log(hiddenApps);

  return (
    <>
      <div className={styles.root}>
        <div>
          {showInitialLoader ? (
            <div className={styles.loading}>
              <Loader size={40} />
            </div>
          ) : (
            <>
              {hiddenApps?.results.length === 0 ? (
                <div className={styles.noMatches}>
                  <img
                    src="/img/Illustrations.jpg"
                    width="250px"
                    height="250px"
                    alt="no results"
                  />
                  <h2>No Hidden Apps Found</h2>
                  <p>
                    This page doesn't have any hidden apps yet.
                    <br />
                    Hide some apps to see them here.
                  </p>
                </div>
              ) : (
                <HiddenApps
                  hiddenApps={hiddenApps}
                  setHiddenApps={setHiddenApps}
                  loadMoreApps={loadMoreApps}
                  isFetchingMore={isFetchingMore}
                  handleRemoveHiddenApp={handleRemoveHiddenApp}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export { HiddenAppsView };
